import PropTypes from "prop-types";
import { useState, createContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getWpUser } from "../api/wordpress/index";
import { getProfile } from "../services/profile.service";
import {
  createUserDocumentFromAuth,
  db,
  onAuthStateChangedListener,
  signOutUser,
} from "../utils/firebase";
import { deepEqual } from "../utils/functions";
import { doc, getDoc } from "firebase/firestore";

export const UserContext = createContext({});

export const UserContextProvider = ({ children }) => {
  const [user, setUser] = useState(
    JSON.parse(localStorage.getItem("user"))?.user || null
  );
  const [subaccount, setSubaccount] = useState(null);
  const [fsUser, setFsUser] = useState(null);
  const [subAccounts, setSubAccounts] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState(
    JSON.parse(localStorage.getItem("user"))?.token || null
  );

  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState("user");
  const [userProfile, setUserProfile] = useState(null);

  const navigate = useNavigate();

  const getGetSubaccount = async (id) => {
    const docSnap = await getDoc(doc(db, "subaccounts", id));
    return docSnap.data();
  };

  const persistUser = useCallback(
    (data) => {
      setUser(data.user);
      setAccessToken(data.token);
      setIsAuthenticated(!!data?.token);
      localStorage.setItem("user", JSON.stringify(data));
    },
    [setUser, setAccessToken, setIsAuthenticated]
  );

  const persistUpdatedUser = useCallback(
    (data) => {
      const newUser = { ...data };
      setUser(newUser);
      localStorage.setItem(
        "user",
        JSON.stringify({
          token: JSON.parse(localStorage.getItem("user"))?.token,
          user: newUser,
        })
      );
    },
    [setUser]
  );

  const validateUser = useCallback(async () => {
    if (accessToken) {
      const response = await getWpUser(accessToken);
      if (!response || !response.data) {
        throw new Error("Invalid API response: missing data");
      }
      const newuser = response.data;
      if (!deepEqual(newuser, user)) {
        persistUpdatedUser(newuser);
      }
    } else {
      throw new Error("no auth token");
    }
  }, [accessToken, user, persistUpdatedUser]);

  const logoutUser = useCallback(
    (redirectUrl) => {
      setUser(null);
      setFsUser(null);
      setAccessToken(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUserRole("user");
      setUserProfile(null);
      localStorage.clear();
      signOutUser();
      if (redirectUrl) navigate(redirectUrl);
    },
    [setUser, setAccessToken, setIsAuthenticated, navigate]
  );

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUser(user?.user || null);
    setAccessToken(user?.token || null);
  }, [setUser, setAccessToken]);

  useEffect(() => {
    const unsubscribe = onAuthStateChangedListener(async (authuser) => {
      if (authuser) {
        createUserDocumentFromAuth(authuser, user);
        
        try {
          const profileResult = await getProfile(authuser.uid);
          setUserProfile(profileResult.status ? profileResult.data : null);
        } catch (error) {
          console.error(error);
          setUserProfile(null);
        }

        try {
          const tokenResult = await authuser.getIdTokenResult(true); 
          setIsAdmin(tokenResult.claims.admin === true);
          setUserRole(tokenResult.claims.role || "user");
        } catch (error) {
          console.error("Failed to fetch custom claims:", error);
          setIsAdmin(false);
          setUserRole("user");
        }

        setFsUser(authuser);
      } else {
        setIsAdmin(false);
        setUserRole("user");
        setUserProfile(null);
        setFsUser(null);
      }
    });
    return unsubscribe;
  }, [user]);

  useEffect(() => {
    if (user && fsUser?.uid) {
      getGetSubaccount(fsUser.uid).then((data) => {
        setSubaccount(data);
      });
    }
  }, [fsUser, user]);

  return (
    <UserContext.Provider
      value={{
        user,
        fsUser,
        subaccount,
        logoutUser,
        persistUser,
        accessToken,
        isAuthenticated,
        setIsAuthenticated,
        persistUpdatedUser,
        validateUser,
        setFsUser,
        subAccounts,
        setSubAccounts,
        isAdmin,
        userRole,
        userProfile,
        setUserProfile
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

UserContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};